import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Zap, LogOut, LayoutDashboard } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";

const LINKS = [
  { label: "Come Funziona", href: "#come-funziona", testid: "nav-how-link" },
  { label: "Livelli", href: "#livelli", testid: "nav-levels-link" },
  { label: "AI Diagnosi", href: "#ai-diagnosi", testid: "nav-ai-link" },
];

export const scrollToId = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  const onLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-md bg-slate-950/80 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto h-[70px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-home-link" aria-label="Tecnico Auto & Truck home">
          <Logo />
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              onClick={(e) => go(e, l.href)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-volt transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/esperti"
            data-testid="nav-experts-link"
            className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-volt transition-colors duration-200"
          >
            Esperti
          </Link>
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-link"
                className="flex items-center gap-2 bg-volt text-obsidian font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-spark hover:scale-[1.03] transition-all duration-200"
              >
                <LayoutDashboard size={15} /> Dashboard
              </Link>
              <button
                data-testid="nav-logout-button"
                onClick={onLogout}
                aria-label="Esci"
                className="text-slate-500 hover:text-red-400 transition-colors"
              >
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <>
              <Link
                to="/accedi"
                data-testid="nav-login-link"
                className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-volt transition-colors"
              >
                Accedi
              </Link>
              <Link
                to="/accedi?mode=register"
                data-testid="nav-cta-button"
                className="flex items-center gap-2 bg-volt text-obsidian font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-spark hover:scale-[1.03] transition-all duration-200"
              >
                <Zap size={15} /> Apri un Ticket
              </Link>
            </>
          )}
        </nav>
        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-slate-300 hover:text-volt transition-colors"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden backdrop-blur-md bg-slate-950/95 border-t border-white/5 px-6 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`${l.testid}-mobile`}
              onClick={(e) => go(e, l.href)}
              className="font-mono text-sm uppercase tracking-[0.2em] text-slate-300 hover:text-volt transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/esperti"
            data-testid="nav-experts-link-mobile"
            onClick={() => setOpen(false)}
            className="font-mono text-sm uppercase tracking-[0.2em] text-slate-300 hover:text-volt transition-colors"
          >
            Esperti
          </Link>
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-link-mobile"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-2 bg-volt text-obsidian font-semibold text-sm px-5 py-3 rounded-full"
              >
                <LayoutDashboard size={15} /> Dashboard
              </Link>
              <button
                data-testid="nav-logout-button-mobile"
                onClick={onLogout}
                className="flex items-center justify-center gap-2 border border-white/15 text-slate-300 text-sm px-5 py-3 rounded-full"
              >
                <LogOut size={15} /> Esci
              </button>
            </>
          ) : (
            <>
              <Link
                to="/accedi"
                data-testid="nav-login-link-mobile"
                onClick={() => setOpen(false)}
                className="font-mono text-sm uppercase tracking-[0.2em] text-slate-300 hover:text-volt transition-colors text-center"
              >
                Accedi
              </Link>
              <Link
                to="/accedi?mode=register"
                data-testid="nav-cta-button-mobile"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-2 bg-volt text-obsidian font-semibold text-sm px-5 py-3 rounded-full"
              >
                <Zap size={15} /> Apri un Ticket
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
};
